import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface PageIntroProps {
  title: string;
  /**
   * SKE-4300 — an optional word or short phrase from `title`, set in Tiempos
   * Headline italic: the Sked signature move (one italic accent inside a
   * Gotham heading). Must appear VERBATIM in `title`; if it does not, the
   * title renders whole rather than dropping copy.
   *
   * At most 3 italics per rendered view, and this heading is the page's, so
   * a page that sets this should not add another.
   */
  titleHighlight?: string;
  description: string;
  children?: ReactNode;
  className?: string;
}

/**
 * Splits the title around the highlight, gluing any punctuation that
 * immediately follows it INSIDE the italic — trailing punctuation takes the
 * style of the phrase it follows, and must not detach from it.
 */
function renderTitle(title: string, highlight?: string): ReactNode {
  if (!highlight) return title;
  const index = title.indexOf(highlight);
  if (index < 0) return title;

  const before = title.slice(0, index);
  const rest = title.slice(index + highlight.length);
  const glue = rest.match(/^[.,!?;:)\]}…”’'"]+/)?.[0] ?? "";

  return (
    <>
      {before}
      <em className="highlight-serif">{highlight + glue}</em>
      {rest.slice(glue.length)}
    </>
  );
}

export function PageIntro({
  title,
  titleHighlight,
  description,
  children,
  className,
}: PageIntroProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 border-b border-neutral-100 pb-6 sm:flex-row sm:items-end sm:justify-between",
        className
      )}
    >
      <div className="max-w-3xl space-y-2">
        <h1 className="font-gotham text-3xl font-semibold text-neutral-900">
          {renderTitle(title, titleHighlight)}
        </h1>
        <p className="font-inter text-sm leading-6 text-neutral-600">
          {description}
        </p>
      </div>
      {children ? <div className="sm:shrink-0">{children}</div> : null}
    </div>
  );
}
